import React from 'react';

// Utility para conversión de monedas MXN a USD

const EXCHANGE_RATE_API = 'https://api.exchangerate-api.com/v4/latest/USD';

interface ExchangeRates {
  MXN: number;
  USD: number;
}

let cachedRate: number | null = null;
let lastFetch: number = 0;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos — la tasa no cambia tan seguido

/**
 * Obtiene la tasa de cambio USD a MXN desde API externa
 * Implementa caché para evitar requests excesivos
 */
export async function getExchangeRate(): Promise<number> {
  const now = Date.now();
  
  // Usar caché si existe y no ha expirado
  if (cachedRate && (now - lastFetch) < CACHE_DURATION) {
    return cachedRate;
  }

  try {
    const response = await fetch(EXCHANGE_RATE_API);
    const data = await response.json();
    
    if (data.rates && data.rates.MXN) {
      cachedRate = data.rates.MXN;
      lastFetch = now;
      return cachedRate!;
    }
  } catch (error) {
    console.warn('Error fetching exchange rate, using fallback:', error);
  }

  // Fallback rate si falla la API (aprox. 18-20 MXN por USD)
  const fallbackRate = 19.5;
  cachedRate = fallbackRate;
  return fallbackRate;
}

/**
 * Convierte de Pesos Mexicanos a Dólares Estadounidenses
 */
export async function mxnToUsd(amountMXN: number): Promise<number> {
  const rate = await getExchangeRate();
  return Math.round((amountMXN / rate) * 100) / 100; // Redondear a 2 decimales
}

/**
 * Formatea el precio en ambas monedas
 */
export async function formatPriceWithUSD(priceMXN: number): Promise<{
  mxn: string;
  usd: string;
  usdAmount: number;
}> {
  const usdAmount = await mxnToUsd(priceMXN);
  
  return {
    mxn: `$${priceMXN.toLocaleString('es-MX')} MXN`,
    usd: `$${usdAmount.toLocaleString('en-US')} USD`,
    usdAmount
  };
}

/**
 * Hook para usar la conversión de monedas en componentes
 */
export function useCurrencyConverter() {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const convertPrice = async (priceMXN: number) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await formatPriceWithUSD(priceMXN);
      setLoading(false);
      return result;
    } catch (err) {
      setError('Error al convertir moneda');
      setLoading(false);
      return {
        mxn: `$${priceMXN.toLocaleString('es-MX')} MXN`,
        usd: 'Error USD',
        usdAmount: 0
      };
    }
  };

  return { convertPrice, loading, error };
}